import notesService from '../services/notesService';

const state = {
  currentNote: null,
  loading: false,
  error: null,
  // Navigation state
  notesList: [],
  notesListLoading: false,
  currentMatterUid: null,
};

const noteGetters = {
  currentNote: (state, getters, rootState) => {
    if (!state.currentNote) return null;
    
    const note = state.currentNote;
    
    // Enrich note with staff name from StaffStore (capital S)
    if (note.staff_uid && rootState.StaffStore?.staffList) {
      const staff = rootState.StaffStore.staffList.find((s) => s.uid === note.staff_uid);
      if (staff) {
        return {
          ...note,
          staff_name: staff.full_name,
        };
      }
    }
    
    return note;
  },
  isLoading: (state) => state.loading,
  hasError: (state) => !!state.error,
  error: (state) => state.error,
  
  // Navigation getters
  notesList: (state) => state.notesList,
  notesListLoading: (state) => state.notesListLoading,
  currentNoteIndex: (state) => {
    if (!state.currentNote || !state.notesList.length) return -1;
    return state.notesList.findIndex((note) => note.uid === state.currentNote.uid);
  },
  canGoPrev: (state, getters) => getters.currentNoteIndex > 0,
  canGoNext: (state, getters) => {
    const index = getters.currentNoteIndex;
    return index >= 0 && index < state.notesList.length - 1;
  },
  prevNoteUid: (state, getters) => {
    const index = getters.currentNoteIndex;
    if (!getters.canGoPrev) return null;
    return state.notesList[index - 1]?.uid || null;
  },
  nextNoteUid: (state, getters) => {
    const index = getters.currentNoteIndex;
    if (!getters.canGoNext) return null;
    return state.notesList[index + 1]?.uid || null;
  },
  hasMultipleNotes: (state) => state.notesList.length > 1,
};

const mutations = {
  setCurrentNote(state, note) {
    state.currentNote = note;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  clearError(state) {
    state.error = null;
  },
  // Navigation mutations
  setNotesList(state, notes) {
    state.notesList = notes;
  },
  setNotesListLoading(state, loading) {
    state.notesListLoading = loading;
  },
  setCurrentMatterUid(state, matterUid) {
    state.currentMatterUid = matterUid;
  },
  clearNotesList(state) {
    state.notesList = [];
    state.currentMatterUid = null;
  },
};

const actions = {
  /**
   * Load a note by UID for editing
   * @param {Object} context - Vuex context
   * @param {string} noteUid - The note UID to load
   * @returns {Promise<Object>} The loaded note
   */
  async loadNote({ commit }, noteUid) {
    commit('setLoading', true);
    commit('clearError');
    try {
      const note = await notesService.get(noteUid);
      commit('setCurrentNote', note);
      return note;
    } catch (err) {
      commit('setError', err);
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Save a new note
   * @param {Object} context - Vuex context
   * @param {Object} noteData - The note data to save
   * @param {string} noteData.matter_uid - Matter UID (required)
   * @param {string} [noteData.title] - Note title
   * @param {string} [noteData.content] - Note content
   * @param {string} [noteData.entity_uid] - Related entity UID
   * @param {string} [noteData.entity_type] - Related entity type
   * @returns {Promise<Object>} The saved note
   */
  async saveNote({ commit }, noteData) {
    // Saving is tracked by the dialog (its own `submitting` flag). Do NOT set the
    // `loading` state here — `loading` drives the retrieval skeleton, which must not
    // appear while saving.
    commit('clearError');
    try {
      const savedNote = await notesService.add(noteData);
      return savedNote;
    } catch (err) {
      commit('setError', err);
      throw err;
    }
  },

  /**
   * Update an existing note
   * @param {Object} context - Vuex context
   * @param {Object} params - Update parameters
   * @param {string} params.noteUid - The note UID to update
   * @param {Object} params.noteData - The note data to update
   * @param {string} [params.noteData.title] - Note title
   * @param {string} [params.noteData.content] - Note content
   * @returns {Promise<Object>} The updated note
   */
  async updateNote({ commit }, { noteUid, noteData }) {
    // Saving is tracked by the dialog (its own `submitting` flag). Do NOT set the
    // `loading` state here — `loading` drives the retrieval skeleton, which must not
    // appear while saving.
    commit('clearError');
    try {
      const updatedNote = await notesService.update(noteUid, noteData);
      commit('setCurrentNote', updatedNote);
      return updatedNote;
    } catch (err) {
      commit('setError', err);
      throw err;
    }
  },

  /**
   * Clear the current note from state
   */
  clearCurrentNote({ commit }) {
    commit('setCurrentNote', null);
    commit('clearError');
  },

  /**
   * Load notes list for a matter (non-blocking, for navigation)
   * @param {Object} context - Vuex context
   * @param {string} matterUid - The matter UID to fetch notes for
   * @returns {Promise<Array>} The notes list
   */
  async loadNotesList({ commit }, matterUid) {
    commit('setNotesListLoading', true);
    commit('setCurrentMatterUid', matterUid);
    try {
      const result = await notesService.getAll(matterUid, { perPage: 100 });
      const notes = result?.notes || [];
      commit('setNotesList', notes);
      return notes;
    } catch (err) {
      console.error('Failed to load notes list for navigation:', err);
      commit('setNotesList', []);
      return [];
    } finally {
      commit('setNotesListLoading', false);
    }
  },

  /**
   * Navigate to previous note
   * @param {Object} context - Vuex context
   * @returns {Promise<Object|null>} The previous note or null
   */
  async navigateToPrev({ getters, dispatch }) {
    const prevUid = getters.prevNoteUid;
    if (!prevUid) return null;
    
    try {
      await dispatch('loadNote', prevUid);
      return getters.currentNote;
    } catch (err) {
      console.error('Failed to navigate to previous note:', err);
      throw err;
    }
  },

  /**
   * Navigate to next note
   * @param {Object} context - Vuex context
   * @returns {Promise<Object|null>} The next note or null
   */
  async navigateToNext({ getters, dispatch }) {
    const nextUid = getters.nextNoteUid;
    if (!nextUid) return null;
    
    try {
      await dispatch('loadNote', nextUid);
      return getters.currentNote;
    } catch (err) {
      console.error('Failed to navigate to next note:', err);
      throw err;
    }
  },

  /**
   * Clear navigation state
   */
  clearNavigationState({ commit }) {
    commit('clearNotesList');
  },
};

export default {
  namespaced: true,
  state,
  getters: noteGetters,
  mutations,
  actions,
};
